import React from 'react'
import Helmet from 'react-helmet'
import Link from 'gatsby-link'
import Layout from 'components/Layout'
import ImplantIcon from 'components/icons/Implant'
import BondingIcon from 'components/icons/Bonding'
import CrownIcon from 'components/icons/Crown'
import BridgesIcon from 'components/icons/Bridges'
import VeneerIcon from 'components/icons/Veneer'
import DenturesIcon from 'components/icons/Dentures'
import XRayIcon from 'components/icons/XRay'
import FillingIcon from 'components/icons/Filling'
import HygieneIcon from 'components/icons/Hygiene'
import PeriodontalIcon from 'components/icons/Periodontal'
import WisdomTeethIcon from 'components/icons/WisdomTeeth'
import WhiteningIcon from 'components/icons/Whitening'
import RootCanalIcon from 'components/icons/RootCanal'

const treatments = [
  {
    name: 'Bonding',
    icon: BondingIcon,
    iconColor: '#635bff',
    description:
      'Bonding is a quick, painless in-office procedure to repair cracks, chips, gaps between teeth or cover stained teeth to enhance your smile. Bonding can often be done without anesthesia if there is no decay or pulp exposure. The composite resin forms a durable, stain resistant surface that is later smoothed and polished to give it a natural appearance and feel.'
  },
  {
    name: 'Bridges',
    icon: BridgesIcon,
    iconColor: '#11efe3',
    description:
      'We will prescribe a bridge when one or more teeth are missing and there are healthy teeth on both sides of the open space. Bridges can be made from three types of material, solid gold for reliable long wear, porcelain fused to a gold base for strength and beauty, and high-strength metal-free porcelain for optimum cosmetic results.'
  },
  {
    name: 'Crowns',
    icon: CrownIcon,
    iconColor: '#635bff',
    description:
      'Crowns are prescribed when a tooth or teeth are too badly decayed, broken or cracked to be easily restored with a filling. Crowns are normally needed for teeth that have had root canals. With new technology, crowns can be made entirely from high-strength ceramic and porcelain for beautiful cosmetic results.'
  },
  {
    name: 'Dentures',
    icon: DenturesIcon,
    iconColor: '#11efe3',
    description:
      'If you have lost some or all of your natural teeth, dentures can be used to replace your missing teeth \u2013 improving your health as well as your appearance. Dentures prevent the sagging of facial muscles and enable you to eat and speak normally, little things often taken for granted until natural teeth are lost.'
  },
  {
    name: 'X-Rays',
    icon: XRayIcon,
    iconColor: '#635bff',
    description:
      'We use state of the art digital X-rays and digital cameras to help with an accurate diagnosis of any concerns. Digital X-rays use less radiation to get a diagnostic picture of your teeth and they help the environment by eliminating the use of chemicals to process the film.'
  },
  {
    name: 'Fillings',
    icon: FillingIcon,
    iconColor: '#11efe3',
    description:
      'Dental fillings are placed in teeth where a dentist has had to remove tooth structure due to decay or fracture. Today we have several preferred options for tooth restoration including tooth colored composite filling materials and cementable resins and porcelains.'
  },
  {
    name: 'Hygiene',
    icon: HygieneIcon,
    iconColor: '#635bff',
    description:
      'The basis of sound dental care is routine cleaning and maintenance. We customize cleanings on the basis of your periodontal health. We utilize medicated gels along with prescription mouth rinses in problem areas if necessary. Sealants are also available as part of preventive dental care.'
  },
  {
    name: 'Implants',
    icon: ImplantIcon,
    iconColor: '#11efe3',
    description:
      'When it comes to replacing one or more teeth, a dental implant is often the treatment of choice. Implants are biocompatible posts that replace the roots of your missing teeth. They are designed to attach to your jaw bone and become the foundation for a natural looking restoration.'
  },
  {
    name: 'Inlays',
    icon: FillingIcon,
    iconColor: '#635bff',
    description:
      'Dental inlays and onlays are some of the strongest and most lifelike dental restorations available. After preparation, an impression is made and sent to a dental lab. With proper care, your inlay or onlay will increase your overall health and instill confidence for years to come.'
  },
  {
    name: 'Periodontal',
    icon: PeriodontalIcon,
    iconColor: '#11efe3',
    description:
      'Gum disease is usually caused by plaque, a sticky film of bacteria that constantly forms on teeth. If plaque is not removed with thorough daily brushing, gums become irritated and inflamed. The early stage of periodontal disease is called gingivitis, which often results in gums that are red, swollen, and may bleed easily.'
  },
  {
    name: 'Root Canal',
    icon: RootCanalIcon,
    iconColor: '#635bff',
    description:
      'Endodontic treatment, more commonly known as root canal therapy, is necessary when the pulp of your tooth becomes inflamed or infected. The inflammation or infection can have a variety of causes such as deep decay, repeated dental procedures on your tooth, or a crack or chip in the tooth.'
  },
  {
    name: 'Veneers',
    icon: VeneerIcon,
    iconColor: '#11efe3',
    description:
      'Unlike a crown, which covers the entire tooth, a veneer is a thin shell that is placed over the front or visible parts of the tooth. There are two types of veneers, ceramic veneers produced by a dental laboratory and resin-based composite veneers. With proper care, your new veneers will greatly improve your appearance.'
  },
  {
    name: 'Whitening',
    icon: WhiteningIcon,
    iconColor: '#635bff',
    description:
      'Everyone loves a bright, white smile. Today there are a variety of products and procedures available to help you improve the color of your teeth. Some whitening procedures are done in our office with one visit. Our in-office whitening will whiten your teeth more quickly and effectively than over-the-counter options.'
  },
  {
    name: 'Wisdom Teeth',
    icon: WisdomTeethIcon,
    iconColor: '#11efe3',
    description:
      'Wisdom teeth, or third molars, are the last teeth to erupt and are the teeth least needed for good oral health. They can cause gum disease, crowding or other damage to adjacent teeth, decay, and bone destroying cysts. Whether you have symptoms or not, it is important to diagnose existing or potential problems caused by your wisdom teeth.'
  }
]

const TreatmentCard = ({ name, icon: Icon, iconColor, description }) => (
  <div
    className="bg-white rounded-xl overflow-hidden transition-all duration-200 hover:shadow-xl"
    style={{
      boxShadow:
        '0 7px 14px 0 rgba(50, 50, 93, 0.1), 0 3px 6px 0 rgba(0, 0, 0, 0.07)',
      border: '1px solid #e3e8ee'
    }}
  >
    <div className="p-8">
      {/* Icon */}
      <div
        className="flex items-center justify-center mb-6"
        style={{
          width: '56px',
          height: '56px',
          minWidth: '56px',
          minHeight: '56px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${iconColor} 0%, ${iconColor}dd 100%)`
        }}
      >
        <Icon size="1.5rem" color="white" fill="white" />
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-navy mb-3">{name}</h3>

      {/* Description */}
      <p className="text-base text-textSecondary leading-relaxed">
        {description}
      </p>
    </div>
  </div>
)

const TreatmentsPage = () => (
  <Layout>
    <Helmet title="Oregon City Smiles - Treatments" />

    {/* Hero Section */}
    <section
      className="relative overflow-hidden"
      style={{
        background:
          'linear-gradient(135deg, #0a2540 0%, #1a1f36 50%, #635bff 100%)',
        minHeight: '340px'
      }}
    >
      {/* Gradient Mesh Background */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            'radial-gradient(ellipse at 20% 50%, #11efe3 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, #ff80b5 0%, transparent 50%), radial-gradient(ellipse at 40% 20%, #635bff 0%, transparent 40%)'
        }}
      />

      <div
        className="relative z-10 container mx-auto px-6 flex flex-col items-center justify-center text-center"
        style={{
          minHeight: '340px',
          paddingTop: '5rem',
          paddingBottom: '5rem'
        }}
      >
        {/* Badge */}
        <div
          className="inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-6"
          style={{
            background:
              'linear-gradient(135deg, rgba(99, 91, 255, 0.1) 0%, rgba(17, 239, 227, 0.1) 100%)',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          Comprehensive Care
        </div>

        <h1
          className="text-white font-bold mb-4 leading-tight"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            letterSpacing: '-0.03em'
          }}
        >
          Our Treatments
        </h1>

        <p
          className="text-lg leading-relaxed max-w-2xl mx-auto"
          style={{ color: 'rgba(255, 255, 255, 0.8)' }}
        >
          From routine cleanings to advanced procedures, we provide a complete
          range of dental treatments to keep your smile healthy and beautiful.
        </p>
      </div>
    </section>

    {/* Treatments Grid */}
    <section className="py-24 px-6 bg-offWhite">
      <div className="container mx-auto max-w-6xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-4"
            style={{
              background:
                'linear-gradient(135deg, rgba(99, 91, 255, 0.1) 0%, rgba(17, 239, 227, 0.1) 100%)',
              color: '#635bff'
            }}
          >
            Services
          </div>
          <h2 className="text-4xl font-bold text-navy mb-4">What We Offer</h2>
          <p className="text-lg text-textSecondary max-w-2xl mx-auto">
            We combine cutting-edge technology with experienced care to deliver
            the best dental treatments for you and your family.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {treatments.map(treatment => (
            <TreatmentCard key={treatment.name} {...treatment} />
          ))}
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto text-center max-w-3xl">
        {/* Decorative line */}
        <div
          className="mx-auto mb-8 rounded-full"
          style={{
            width: '64px',
            height: '4px',
            background: 'linear-gradient(90deg, #635bff 0%, #11efe3 100%)'
          }}
        />

        <h2 className="text-4xl font-bold text-navy mb-4 leading-tight">
          Ready for Your Visit?
        </h2>

        <p className="text-lg text-textSecondary leading-relaxed mb-10 max-w-2xl mx-auto">
          Our team is here to help you achieve and maintain a healthy, beautiful
          smile. Schedule your appointment today.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/appointments"
            className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-semibold text-base rounded-lg transition-all duration-150 hover:bg-primaryDark"
            style={{
              boxShadow: '0 4px 14px 0 rgba(99, 91, 255, 0.39)'
            }}
          >
            Schedule an Appointment
          </Link>
          <Link
            to="/about_us"
            className="inline-flex items-center justify-center px-8 py-4 font-semibold text-base rounded-lg transition-all duration-150 text-textPrimary hover:bg-offWhite"
            style={{
              border: '1px solid #e3e8ee'
            }}
          >
            Meet Our Team
            <svg
              className="w-4 h-4 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  </Layout>
)

export default TreatmentsPage
